// src/App.js
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { store } from './store/index';
import { restoreSession } from './store/authSlice';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import PageTitle from './components/PageTitle';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import GlobalSnackbar from './components/GlobalSnackbar';
import AIAdvisorHome from './pages/AIAdvisorHome';
import ResponsiveModal from './components/ResponsiveModal';
import './App.css';

// Route-level code splitting (keep home eager to preserve current initial-load UX)
const Conversation = lazy(() => import('./pages/Conversation'));
const DashboardsHome = lazy(() => import('./pages/DashboardsHome'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const EmailVerification = lazy(() => import('./components/EmailVerification'));
const PasswordReset = lazy(() => import('./components/PasswordReset'));
const TestStreaming = lazy(() => import('./pages/TestStreaming'));
const DashboardShareAccept = lazy(() => import('./pages/DashboardShareAccept'));

function AppRoutes() {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 900);
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth >= 1210);
  const location = useLocation();
  
  // State for conversation header integration
  const [currentQuery, setCurrentQuery] = useState('');
  const [hasPrevMessage, setHasPrevMessage] = useState(false);
  const [hasNextMessage, setHasNextMessage] = useState(false);
  const [viewMode, setViewMode] = useState('output');
  const [prevArrowHandler, setPrevArrowHandler] = useState(null);
  const [nextArrowHandler, setNextArrowHandler] = useState(null);
  const [isFetchingMessages, setIsFetchingMessages] = useState(false);
  const [closeOutputHandler, setCloseOutputHandler] = useState(null);
  const [resendHandler, setResendHandler] = useState(null);
  const [backToGridMode, setBackToGridMode] = useState(false);
  const [viewFinalDashboardHandler, setViewFinalDashboardHandler] = useState(null);
  const [showViewFinalDashboard, setShowViewFinalDashboard] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    // Check for existing session on app load
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        dispatch(restoreSession(userData));
      } catch (error) {
        console.error('Error restoring session:', error);
        localStorage.removeItem('user');
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsSmallScreen(w < 1020);
      setIsWideScreen(w >= 1210);
    };
  
    window.addEventListener('resize', handleResize);
    handleResize();
  
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Conversation state handlers
  const handleConversationStateChange = useCallback((
    query,
    hasPrev,
    hasNext,
    mode,
    prevArrowHandler,
    nextArrowHandler,
    isFetching,
    onResend,
    onCloseOutput,
    showBackToGrid = false,
    onViewFinalDashboard = null,
    showViewDashboard = false
  ) => {
    setCurrentQuery(query);
    setHasPrevMessage(hasPrev);
    setHasNextMessage(hasNext);
    setViewMode(mode);
    setPrevArrowHandler(() => prevArrowHandler);
    setNextArrowHandler(() => nextArrowHandler);
    setIsFetchingMessages(isFetching);
    setResendHandler(() => onResend);
    setCloseOutputHandler(() => onCloseOutput || null);
    setBackToGridMode(showBackToGrid);
    setViewFinalDashboardHandler(() => onViewFinalDashboard);
    setShowViewFinalDashboard(showViewDashboard);
  }, []);

  const handlePrevArrowClick = useCallback(() => {
    if (prevArrowHandler) prevArrowHandler();
  }, [prevArrowHandler]);

  const handleNextArrowClick = useCallback(() => {
    if (nextArrowHandler) nextArrowHandler();
  }, [nextArrowHandler]);

  // Check if we're on the home page
  const isPinnedPage = (location.pathname === '/' || viewMode === 'chat') && isWideScreen;

  // Sidebar is pinned on home page, unpinned elsewhere
  const sidebarWidth = isPinnedPage ? 225 : 65;

  return (
    <>
    <div className="app-container">
      <GlobalSnackbar />
      <ResponsiveModal open={isSmallScreen} />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/user/verify-email" element={<EmailVerification />} />
          <Route path="/password-reset" element={<PasswordReset />} />
          <Route path="/*" element={
              <>
                <Sidebar
                  isPinned={isPinnedPage}
                  viewMode={viewMode}
                  onRequestStartView={() => setViewMode('start')}
                />
                <div className="main-content" style={{ marginLeft: sidebarWidth }}>
                  <div className='header-container'>
                    <Header
                      width={sidebarWidth}
                      currentQuery={currentQuery}
                      hasPrevMessage={hasPrevMessage}
                      hasNextMessage={hasNextMessage}
                      onPrevArrowClick={handlePrevArrowClick}
                      onNextArrowClick={handleNextArrowClick}
                      viewMode={viewMode}
                      isFetchingMessages={isFetchingMessages}
                      onRefreshClick={() => resendHandler && resendHandler()}
                      onCloseOutputView={() => closeOutputHandler && closeOutputHandler()}
                      backToGridMode={backToGridMode}
                      onViewFinalDashboard={() => viewFinalDashboardHandler && viewFinalDashboardHandler()}
                      showViewFinalDashboard={showViewFinalDashboard}
                    />
                  </div>
                  <div className="page-content">
                    <Routes>
                      <Route path="/" element={
                        <>
                          <PageTitle title="Vester" />
                          <AIAdvisorHome />
                        </>
                      } />
                      <Route path="/conversation/new" element={
                        <>
                          <PageTitle title="Vester" />
                          <Conversation
                            onConversationStateChange={handleConversationStateChange}
                          />
                        </>
                      } />
                      <Route path="/conversation/:conversationId" element={
                        <>
                          <PageTitle title="Vester" />
                          <Conversation
                            onConversationStateChange={handleConversationStateChange}
                          />
                        </>
                      } />
                      <Route path="/my-dashboards" element={
                        <>
                          <PageTitle title="Vester - My Dashboards" />
                          <DashboardsHome />
                        </>
                      } />
                      <Route path="/teststreaming" element={
                        <>
                          <PageTitle title="Test Streaming" />
                          <TestStreaming />
                        </>
                      } />
                      <Route path="/dashboard/:dashboardId" element={
                        <>
                          <PageTitle title="Vester - Dashboard" />
                          <Dashboard />
                        </>
                      } />
                    <Route path="/dashboard/share/accept" element={
                      <>
                        <PageTitle title="Vester - Accept Share" />
                        <DashboardShareAccept />
                      </>
                    } />
                    </Routes>
                  </div>
                </div>
              </>
            } />
          </Routes>
        </Suspense>
      </div>
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <Router>
        <AppRoutes />
      </Router>
    </Provider>
  );
}

export default App;
